import React, {
  useState,
  useRef,
  useCallback,
  useEffect,
  useContext,
} from "react";
import { compose } from "recompose";
import { withAuthorization, AuthUserContext } from "../Session";
import { withFirebase } from "../Firebase";
import NavigationMenus from "../NavigationMenus";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Paper from "@material-ui/core/Paper";
import CircularProgress from "@material-ui/core/CircularProgress";
import VideocamIcon from "@material-ui/icons/Videocam";
import StopIcon from "@material-ui/icons/Stop";
import ReplayIcon from "@material-ui/icons/Replay";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import { Grid, useMediaQuery, useTheme } from "@material-ui/core";
import axios from "axios";
import Helpers from "../Helpers";
import CustomButton from "../Button";
import { getStorageDownloadURL } from "../Firebase/firebase";
import * as ROUTES from "../../constants/route";
import AttestationGuideImage from "./attestation-guide.png";
import { getVideoExtension, normalizeVideoMimeType } from "./videoFormat";
import { resolveAttestationVideoUrl } from "./videoSource";
import { useHistory } from "react-router-dom";
import {
  AttestationStatusNotice,
  isAttestationEligible,
  useAttestationEligibility,
} from "./useAttestationEligibility";

const MAX_DURATION_SECONDS = 60;
const RAMPVER_GREEN = "#256141";
const INSTRUCTION_VIDEO_EMBED_URL =
  "https://www.youtube.com/embed/kSqQXsVcGU0";
const INSTRUCTION_VIDEO_URL = "https://www.youtube.com/shorts/kSqQXsVcGU0";
const READABLE_DATE_FORMATTER = new Intl.DateTimeFormat("en-US", {
  month: "long",
  day: "numeric",
  year: "numeric",
});
const SCRIPT_VALUE_STYLE = {
  color: RAMPVER_GREEN,
  fontWeight: 700,
};
const SCRIPT_PLACEHOLDER_STYLE = {
  color: "#6b7280",
  fontWeight: 600,
};

const getFormattedDate = (value) => {
  if (!value) return "";

  try {
    if (typeof value?.toDate === "function") {
      return READABLE_DATE_FORMATTER.format(value.toDate());
    }

    const parsedDate = value instanceof Date ? value : new Date(value);

    if (Number.isNaN(parsedDate.getTime())) {
      return "";
    }

    return READABLE_DATE_FORMATTER.format(parsedDate);
  } catch {
    return "";
  }
};

const getFullName = (name = {}) =>
  [name.firstName, name.middleName, name.lastName, name.suffix]
    .map((part) => (typeof part === "string" ? part.trim() : ""))
    .filter(Boolean)
    .join(" ");

const getSubmittedFrontIdPath = (idImages = []) => {
  const frontId = idImages.find(
    (item) => item?.image === "idPhotoFront" || item?.image === "secondary1"
  );

  return frontId?.url || null;
};

const VideoAttestationBase = (props) => {
  const authUser = useContext(AuthUserContext);
  const history = useHistory();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isEmbedded = Boolean(props.embedded || props.nextStep);
  const uploadContext = isEmbedded ? "long-kyc" : "video-only";
  const account = useAttestationEligibility(props.firebase, authUser?.uid, uploadContext);
  const { refresh: refreshAccount, eligibleRef } = account;
  const [stateRefused, setStateRefused] = useState(false);
  const [recordingState, setRecordingState] = useState("idle");
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [recordedUrl, setRecordedUrl] = useState(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [errorMessage, setErrorMessage] = useState("");
  const [cameraError, setCameraError] = useState("");
  const [loadingMeta, setLoadingMeta] = useState(true);
  const [metaError, setMetaError] = useState("");
  const [metaRetry, setMetaRetry] = useState(0);
  const [checkingAction, setCheckingAction] = useState(false);
  const [existingVideoUrl, setExistingVideoUrl] = useState(null);
  const [loadingVideo, setLoadingVideo] = useState(false);
  const [showExistingVideo, setShowExistingVideo] = useState(false);
  const [kycData, setKycData] = useState(null);
  const [idPreviewOpen, setIdPreviewOpen] = useState(false);
  const [idPreviewUrl, setIdPreviewUrl] = useState(null);
  const [idPreviewLoading, setIdPreviewLoading] = useState(false);
  const [idPreviewError, setIdPreviewError] = useState("");

  const liveVideoRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const redirectTimerRef = useRef(null);
  const cameraStartingRef = useRef(false);
  const actionRef = useRef(false);
  const helpersRef = useRef(new Helpers());

  const startCamera = useCallback(async () => {
    if (!eligibleRef.current || streamRef.current || cameraStartingRef.current) return;
    cameraStartingRef.current = true;
    try {
      setCameraError("");
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: "user",
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
        audio: true,
      });
      if (!eligibleRef.current) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }
      streamRef.current = stream;
      if (liveVideoRef.current) {
        liveVideoRef.current.srcObject = stream;
      }
    } catch (err) {
      setCameraError(
        "Unable to access your camera and microphone. Please allow browser access to your camera and microphone, then try again."
      );
    } finally {
      cameraStartingRef.current = false;
    }
  }, [eligibleRef]);

  const stopCamera = useCallback(() => {
    if (liveVideoRef.current) {
      liveVideoRef.current.pause();
      liveVideoRef.current.srcObject = null;
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      stopCamera();
      if (timerRef.current) clearInterval(timerRef.current);
      if (redirectTimerRef.current) clearTimeout(redirectTimerRef.current);
    };
  }, [stopCamera]);

  useEffect(() => {
    if (!authUser?.uid || !props.firebase) return;
    let active = true;
    setLoadingMeta(true);
    setMetaError("");
    setLoadingVideo(true);
    props.firebase.db
      .collection("users")
      .doc(authUser.uid)
      .collection("KYC")
      .doc("KYC")
      .get({ source: "server" })
      .then(async (doc) => {
        if (!active) return;
        const data = doc.data() ?? null;
        setKycData(data);
        const meta = data?.attestationVideo ?? null;

        if (meta) {
          try {
            const url = await resolveAttestationVideoUrl(
              meta,
              getStorageDownloadURL,
            );
            if (active && url) {
              setExistingVideoUrl(url);
              setShowExistingVideo(true);
            }
          } catch {
            if (active) setExistingVideoUrl(null);
          }
        }
      })
      .catch(() => {
        if (active) setMetaError("We couldn't load your verification details. Please retry before continuing.");
      })
      .finally(() => {
        if (!active) return;
        setLoadingMeta(false);
        setLoadingVideo(false);
      });
    return () => { active = false; };
  }, [authUser?.uid, props.firebase, metaRetry]);

  const isAttestationExcluded = kycData?.attestationVideoExcluded === true;
  const hasExistingVideo = showExistingVideo && !!existingVideoUrl;
  const hasCompletedAttestation = hasExistingVideo || isAttestationExcluded;

  // Only start camera if there is no existing video to show
  useEffect(() => {
    if (!account.eligible || stateRefused || metaError) {
      if (timerRef.current) clearInterval(timerRef.current);
      if (mediaRecorderRef.current?.state === "recording") mediaRecorderRef.current.stop();
      stopCamera();
      return;
    }
    if (loadingMeta || loadingVideo) return;
    if (!showExistingVideo && !isAttestationExcluded && recordingState === "idle") {
      startCamera();
    }
  }, [
    loadingMeta,
    loadingVideo,
    showExistingVideo,
    isAttestationExcluded,
    account.eligible,
    stateRefused,
    metaError,
    recordingState,
    startCamera,
    stopCamera,
  ]);

  useEffect(() => {
    return () => {
      if (recordedUrl) URL.revokeObjectURL(recordedUrl);
    };
  }, [recordedUrl]);

  const surfacePadding = isMobile ? 16 : 24;
  const containerMaxWidth = "100%";
  const mediaFrameMaxWidth = isMobile ? 320 : 640;
  const mediaFramePaddingTop = isMobile ? "177.78%" : "56.25%";
  const contentPadding = isMobile ? "24px 0" : "32px 0";

  const getActionButtonStyle = (extraStyles = {}) => ({
    backgroundColor: RAMPVER_GREEN,
    color: "#ffffff",
    borderColor: RAMPVER_GREEN,
    textTransform: "none",
    fontWeight: 600,
    borderRadius: 8,
    ...(isMobile ? { width: "100%" } : {}),
    ...extraStyles,
  });

  const getButtonGroupStyle = (extraStyles = {}) => ({
    display: "flex",
    flexDirection: isMobile ? "column" : "row",
    justifyContent: "center",
    alignItems: isMobile ? "stretch" : "center",
    gap: 12,
    width: "100%",
    maxWidth: mediaFrameMaxWidth,
    margin: "16px auto 0",
    ...extraStyles,
  });

  useEffect(() => {
    if (props.onStatusChange) {
      props.onStatusChange(recordingState === "success" || hasCompletedAttestation);
    }
  }, [props.onStatusChange, recordingState, hasCompletedAttestation]);

  useEffect(() => {
    if ((recordingState !== "success" && !isAttestationExcluded) || isEmbedded ||
        !account.loaded || account.error || metaError) {
      return undefined;
    }

    redirectTimerRef.current = setTimeout(() => {
      history.replace(ROUTES.DASHBOARD);
    }, 2500);

    return () => {
      if (redirectTimerRef.current) {
        clearTimeout(redirectTimerRef.current);
        redirectTimerRef.current = null;
      }
    };
  }, [history, isEmbedded, isAttestationExcluded, recordingState, account.loaded, account.error, metaError]);

  const checkEligibility = useCallback(async () => {
    const data = await refreshAccount();
    return data && eligibleRef.current && isAttestationEligible(data.stats, uploadContext);
  }, [refreshAccount, eligibleRef, uploadContext]);

  const handleContinue = async () => {
    if (actionRef.current || stateRefused) return;
    actionRef.current = true;
    setCheckingAction(true);
    try {
      if (!await checkEligibility()) return;
      props.fixedStep(5);
      props.nextStep();
    } finally {
      actionRef.current = false;
      setCheckingAction(false);
    }
  };

  const getSupportedMimeType = useCallback(() => {
    const types = [
      "video/webm;codecs=vp9,opus",
      "video/webm;codecs=vp8,opus",
      "video/webm",
      "video/mp4",
    ];
    return types.find((type) => MediaRecorder.isTypeSupported(type)) || "";
  }, []);

  const startRecording = useCallback(async () => {
    if (actionRef.current || !streamRef.current || stateRefused) return;
    actionRef.current = true;
    setCheckingAction(true);
    const eligible = await checkEligibility();
    actionRef.current = false;
    setCheckingAction(false);
    if (!eligible || !streamRef.current) return;

    chunksRef.current = [];
    setElapsedSeconds(0);
    setRecordedBlob(null);
    setRecordedUrl(null);
    setErrorMessage("");

    const mimeType = getSupportedMimeType();
    const recorder = new MediaRecorder(streamRef.current, {
      ...(mimeType ? { mimeType } : {}),
    });
    mediaRecorderRef.current = recorder;

    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunksRef.current.push(e.data);
    };

    recorder.onstop = () => {
      const uploadMimeType =
        normalizeVideoMimeType(recorder.mimeType) ||
        normalizeVideoMimeType(chunksRef.current[0]?.type) ||
        normalizeVideoMimeType(mimeType);

      if (!uploadMimeType) {
        setErrorMessage(
          "Your browser recorded an unsupported video format. Please try another browser."
        );
        setRecordingState("error");
        stopCamera();
        return;
      }

      const blob = new Blob(chunksRef.current, {
        type: uploadMimeType,
      });
      const url = URL.createObjectURL(blob);
      setRecordedBlob(blob);
      setRecordedUrl(url);
      setRecordingState("preview");
      stopCamera();
    };

    recorder.start(1000);
    setRecordingState("recording");

    let seconds = 0;
    timerRef.current = setInterval(() => {
      seconds++;
      setElapsedSeconds(seconds);
      if (seconds >= MAX_DURATION_SECONDS) {
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }
        if (
          mediaRecorderRef.current &&
          mediaRecorderRef.current.state !== "inactive"
        ) {
          mediaRecorderRef.current.stop();
        }
      }
    }, 1000);
  }, [getSupportedMimeType, stopCamera, checkEligibility, stateRefused]);

  const stopRecording = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state !== "inactive"
    ) {
      mediaRecorderRef.current.stop();
    }
  }, []);

  const handleReRecord = useCallback(async () => {
    if (actionRef.current || stateRefused) return;
    actionRef.current = true;
    setCheckingAction(true);
    const eligible = await checkEligibility();
    actionRef.current = false;
    setCheckingAction(false);
    if (!eligible) return;
    setRecordingState("idle");
    setRecordedBlob(null);
    setRecordedUrl(null);
    setElapsedSeconds(0);
    setErrorMessage("");
    setShowExistingVideo(false);
  }, [checkEligibility, stateRefused]);

  const handleRetryCamera = async () => {
    if (await checkEligibility()) await startCamera();
  };

  const handleUpload = useCallback(async () => {
    if (!recordedBlob || !authUser?.uid || actionRef.current || stateRefused) return;
    actionRef.current = true;
    setCheckingAction(true);
    if (!await checkEligibility()) {
      actionRef.current = false;
      setCheckingAction(false);
      return;
    }

    setRecordingState("uploading");
    setErrorMessage("");

    try {
      const token = helpersRef.current.getCookie("token");
      const formData = new FormData();
      const extension = getVideoExtension(recordedBlob.type);

      if (!extension) {
        throw new Error("Unsupported recorded video format");
      }

      formData.append("video", recordedBlob, `attestation-video.${extension}`);
      formData.append("uploadContext", uploadContext);

      await axios.post(
        `${ROUTES.FUNCTION_BASE_URL}v2/attestation/${authUser.uid}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setRecordingState("success");
      // Older successful responses have no data.status; always read the root doc.
      await refreshAccount();
    } catch (err) {
      if ([403, 409].includes(err.response?.status)) {
        setStateRefused(true);
        setRecordingState("preview");
        await refreshAccount();
        return;
      }
      setRecordingState("error");
      setErrorMessage(
        err.response?.data?.message ||
          "Failed to upload video. Please try again."
      );
    } finally {
      actionRef.current = false;
      setCheckingAction(false);
    }
  }, [recordedBlob, authUser, stateRefused, checkEligibility, refreshAccount, uploadContext]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  };

  const isPreviewState =
    recordingState === "preview" ||
    recordingState === "uploading" ||
    recordingState === "success" ||
    recordingState === "error";
  const formattedFullName = getFullName(kycData?.name);
  const formattedBirthDate = getFormattedDate(kycData?.dateOfBirth);
  const formattedToday = READABLE_DATE_FORMATTER.format(new Date());
  const scriptFullName = formattedFullName || "[Full Name]";
  const scriptBirthDate = formattedBirthDate || "[Birthday]";
  const scriptToday = formattedToday || "[Date Today]";
  const submittedFrontIdPath = getSubmittedFrontIdPath(kycData?.idImages || []);

  const handleOpenIdPreview = useCallback(async () => {
    setIdPreviewOpen(true);
    setIdPreviewError("");

    if (!submittedFrontIdPath) {
      setIdPreviewLoading(false);
      setIdPreviewUrl(null);
      setIdPreviewError(
        "We couldn't find the submitted front side of the ID for this application."
      );
      return;
    }

    if (idPreviewUrl) return;

    setIdPreviewLoading(true);

    try {
      const downloadUrl = await getStorageDownloadURL(submittedFrontIdPath);

      setIdPreviewUrl(downloadUrl);
    } catch {
      setIdPreviewUrl(null);
      setIdPreviewError(
        "We couldn't load the submitted ID preview right now. Please try again later."
      );
    } finally {
      setIdPreviewLoading(false);
    }
  }, [idPreviewUrl, submittedFrontIdPath]);

  const renderScriptText = () => (
    <>
      {"I am "}
      <span
        style={
          formattedFullName ? SCRIPT_VALUE_STYLE : SCRIPT_PLACEHOLDER_STYLE
        }
      >
        {scriptFullName}
      </span>
      {", born on "}
      <span
        style={
          formattedBirthDate ? SCRIPT_VALUE_STYLE : SCRIPT_PLACEHOLDER_STYLE
        }
      >
        {scriptBirthDate}
      </span>
      {
        ". I am confirming my request to verify my Rampver Online account today, "
      }
      <span style={SCRIPT_VALUE_STYLE}>{scriptToday}</span>
      {"."}
    </>
  );

  if (!account.loaded || !account.eligible || stateRefused || metaError) {
    return (
      <AttestationStatusNotice
        account={{ ...account, error: metaError || account.error || (stateRefused && account.eligible
          ? "Your account cannot submit a video right now. Refresh your status or contact online@rampver.com for help."
          : "") }}
        history={history}
        onRetry={async () => {
          await refreshAccount();
          if (metaError) setMetaRetry((value) => value + 1);
          setStateRefused(false);
        }}
      >
        {isAttestationExcluded && <p>Your video attestation has been waived.</p>}
        {existingVideoUrl && <video aria-label="Your submitted video" src={existingVideoUrl} controls playsInline preload="metadata" style={{ width: "100%", maxWidth: mediaFrameMaxWidth }} />}
      </AttestationStatusNotice>
    );
  }

  if (loadingMeta || loadingVideo) {
    return <div role="status"><CircularProgress size={24} /> Loading your verification details...</div>;
  }

  if (isAttestationExcluded) {
    return (
      <div
        style={{
          width: "100%",
          maxWidth: containerMaxWidth,
          margin: "0 auto",
          padding: contentPadding,
          boxSizing: "border-box",
        }}
      >
        <Paper style={{ padding: surfacePadding, marginBottom: 24 }} elevation={2}>
          <h1
            style={{ fontSize: isMobile ? 22 : 24, fontWeight: 700, marginBottom: 8 }}
            className="font-poppins"
          >
            Video Attestation
          </h1>
          <p className="font-poppins" style={{ color: "#374151", marginBottom: 0 }}>
            Your video attestation has been waived. You can continue with your verification.
          </p>
        </Paper>

        {props.nextStep && (
          <Grid item xs={12} style={{ marginTop: 24 }}>
            <div className="flex">
              <div className="btn-back" onClick={props.prevStep}>
                Previous Step
              </div>
              <CustomButton
                text="Continue"
                type="button"
                buttonTheme="primary"
                 disabled={account.checking || checkingAction}
                 onClick={handleContinue}
              />
            </div>
          </Grid>
        )}
      </div>
    );
  }

  return (
    <div
      style={{
        width: "100%",
        maxWidth: containerMaxWidth,
        margin: "0 auto",
        padding: contentPadding,
        boxSizing: "border-box",
      }}
    >
      <Paper style={{ padding: surfacePadding, marginBottom: 24 }} elevation={2}>
        <h1
          style={{ fontSize: isMobile ? 22 : 24, fontWeight: 700, marginBottom: 8 }}
          className="font-poppins"
        >
          Video Attestation
        </h1>
        <p className="font-poppins" style={{ color: "#374151", marginBottom: 20 }}>
          Please use your phone or computer to record a short video and
          follow the steps below.
        </p>

        {/* Step 1 */}
        <div
          style={{
            backgroundColor: "#f5f5f5",
            borderRadius: 12,
            padding: isMobile ? 16 : 20,
            marginBottom: 16,
            boxShadow: "0px 3px 1px -2px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 1px 5px 0px rgba(0,0,0,0.12)",
          }}
        >
          <p className="font-poppins" style={{ fontWeight: 700, fontSize: 16, color: "#111827", marginBottom: 8 }}>
            Step 1: Hold Your Valid ID
          </p>
          <ul className="font-poppins" style={{ listStyleType: "disc", paddingLeft: isMobile ? 20 : 24, color: "#374151", margin: 0 }}>
            <li style={{ marginBottom: 4 }}>
              Hold the same valid ID you submitted during identity verification.
            </li>
            <li>
              Make sure both your face and the ID are clearly visible in the frame.
            </li>
          </ul>
          <div style={{ margin: "12px 0 0", textAlign: "center" }}>
            <img
              src={AttestationGuideImage}
              alt="Video attestation guide"
              style={{
                maxWidth: "100%",
                borderRadius: 8,
              }}
            />
          </div>
        </div>

        {/* Step 2 */}
        <div
          style={{
            backgroundColor: "#f5f5f5",
            borderRadius: 12,
            padding: isMobile ? 16 : 20,
            marginBottom: 16,
            boxShadow: "0px 3px 1px -2px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 1px 5px 0px rgba(0,0,0,0.12)",
          }}
        >
          <p className="font-poppins" style={{ fontWeight: 700, fontSize: 16, color: "#111827", marginBottom: 8 }}>
            Step 2: Watch the Sample and Record Your Video
          </p>
          <p className="font-poppins" style={{ color: "#374151", marginBottom: 8 }}>
            Watch the sample first, then record yourself while holding your ID and saying the script below.
          </p>
          <ul className="font-poppins" style={{ listStyleType: "disc", paddingLeft: isMobile ? 20 : 24, color: "#4b5563", margin: 0 }}>
            <li style={{ marginBottom: 4 }}>
              Allow your browser to access your camera and microphone before starting.
            </li>
            <li style={{ marginBottom: 4 }}>
              Make sure the video is well-lit and your voice is clear.
            </li>
            <li style={{ marginBottom: 4 }}>
              Recording must be continuous with no cuts or edits. Keep it under one minute.
            </li>
            <li>
              Keep your face and ID visible in the frame.
            </li>
          </ul>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "minmax(0, 1fr) minmax(0, 1fr)",
              gap: 16,
              alignItems: "start",
              margin: "16px 0 0",
            }}
          >
            <div>
              <p
                className="font-poppins"
                style={{
                  marginBottom: 8,
                  fontWeight: 600,
                  color: "#111827",
                }}
              >
                Sample video
              </p>
              <div
                style={{
                  position: "relative",
                  width: "100%",
                  borderRadius: 8,
                  overflow: "hidden",
                  backgroundColor: "#000",
                  paddingTop: mediaFramePaddingTop,
                }}
              >
                <iframe
                  title="Video attestation instructions"
                  src={INSTRUCTION_VIDEO_EMBED_URL}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    border: 0,
                  }}
                />
              </div>
              <a
                className="font-poppins"
                href={INSTRUCTION_VIDEO_URL}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  marginTop: 12,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  color: RAMPVER_GREEN,
                  fontSize: 14,
                  fontWeight: 600,
                  textDecoration: "none",
                }}
              >
                <OpenInNewIcon style={{ fontSize: 18 }} />
                Watch on YouTube
              </a>
            </div>

            <div>
              <p
                className="font-poppins"
                style={{
                  marginBottom: 8,
                  fontWeight: 600,
                  color: "#111827",
                }}
              >
                {isPreviewState ? "Recorded video" : "Camera preview"}
              </p>

              {hasExistingVideo ? (
                <div
                  className="font-poppins"
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    gap: 10,
                    padding: isMobile ? 16 : 20,
                    backgroundColor: "#ffffff",
                    borderRadius: 8,
                    border: "1px solid #e5e7eb",
                    minHeight: isMobile ? 220 : 280,
                    color: "#374151",
                  }}
                >
                  <span style={{ fontWeight: 700, color: "#111827" }}>
                    Submitted video found
                  </span>
                  <span>
                    Your previously submitted video is shown below. You can
                    re-record if you need to replace it.
                  </span>
                </div>
              ) : cameraError ? (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 16,
                    padding: "32px 16px",
                    backgroundColor: "#ffffff",
                    borderRadius: 8,
                    border: "1px solid #e5e7eb",
                    minHeight: isMobile ? 220 : 280,
                  }}
                >
                  <ErrorOutlineIcon style={{ fontSize: 48, color: "#ef4444" }} />
                  <p
                    className="font-poppins"
                    style={{ color: "#ef4444", textAlign: "center", margin: 0 }}
                  >
                    {cameraError}
                  </p>
                  <Button
                    variant="contained"
                    onClick={handleRetryCamera}
                    size="large"
                    fullWidth={isMobile}
                    style={getActionButtonStyle()}
                  >
                    Retry Camera Access
                  </Button>
                </div>
              ) : (
                <>
                  <div
                    style={{
                      position: "relative",
                      borderRadius: 8,
                      overflow: "hidden",
                      backgroundColor: "#000",
                      paddingTop: mediaFramePaddingTop,
                    }}
                  >
                    {isPreviewState ? (
                      <video
                        key={recordedUrl || "preview-video"}
                        src={recordedUrl}
                        controls
                        playsInline
                        preload="metadata"
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      <video
                        key="live-camera"
                        ref={liveVideoRef}
                        autoPlay
                        muted
                        playsInline
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          transform: "scaleX(-1)",
                        }}
                      />
                    )}

                    {recordingState === "recording" && (
                      <div
                        style={{
                          position: "absolute",
                          top: isMobile ? 12 : 16,
                          left: isMobile ? 12 : 16,
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          backgroundColor: "rgba(0,0,0,0.6)",
                          borderRadius: 20,
                          padding: "4px 12px",
                        }}
                      >
                        <div
                          style={{
                            width: 12,
                            height: 12,
                            borderRadius: "50%",
                            backgroundColor: "#ef4444",
                            animation: "pulse 1.5s ease-in-out infinite",
                          }}
                        />
                        <span
                          className="font-poppins"
                          style={{ color: "#fff", fontSize: 14 }}
                        >
                          REC
                        </span>
                      </div>
                    )}

                  </div>

                  {(recordingState === "recording" ||
                    recordingState === "preview") && (
                    <div
                      className="font-poppins"
                      style={{
                        textAlign: "center",
                        margin: "12px auto 0",
                        fontSize: isMobile ? 16 : 18,
                      }}
                    >
                      {formatTime(elapsedSeconds)}
                      {recordingState === "recording" && (
                        <span style={{ color: "#9ca3af" }}>
                          {" "}/ {formatTime(MAX_DURATION_SECONDS)}
                        </span>
                      )}
                    </div>
                  )}

                  <div
                    className="font-poppins"
                    style={{
                      marginTop: 12,
                      padding: isMobile ? 16 : 18,
                      background: "linear-gradient(135deg, #fff8e1 0%, #f4fbf6 100%)",
                      borderRadius: 14,
                      border: "1px solid #ecd79b",
                      color: "#111827",
                      lineHeight: 1.6,
                      boxShadow: "0 10px 24px rgba(37,97,65,0.08)",
                    }}
                  >
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        padding: "4px 10px",
                        borderRadius: 999,
                        backgroundColor: "rgba(37,97,65,0.1)",
                        color: RAMPVER_GREEN,
                        fontSize: 11,
                        fontWeight: 700,
                        letterSpacing: 0.8,
                        textTransform: "uppercase",
                        marginBottom: 10,
                      }}
                    >
                      Script Guide
                    </span>
                    <span
                      style={{
                        display: "block",
                        fontSize: 12,
                        color: "#6b7280",
                        marginBottom: 6,
                      }}
                    >
                      Start recording and say this in the camera while holding your government ID used in your application.
                    </span>
                    <span
                      style={{
                        display: "block",
                        fontSize: 12,
                        fontStyle: "italic",
                        color: "#4b5563",
                        marginBottom: 10,
                      }}
                    >
                      If you need a reference, use the preview button to check the ID you submitted.
                    </span>
                    <div
                      style={{
                        marginTop: 4,
                        padding: isMobile ? 16 : 20,
                        backgroundColor: "rgba(255,255,255,0.92)",
                        borderRadius: 12,
                        border: "1px solid #ecd79b",
                        borderLeft: "6px solid #d4a72c",
                        boxShadow: "0 8px 20px rgba(0,0,0,0.05)",
                        lineHeight: 1.8,
                      }}
                    >
                      {renderScriptText()}
                    </div>
                    <div
                      style={getButtonGroupStyle({
                        maxWidth: "100%",
                        marginTop: 16,
                      })}
                    >
                      <Button
                        variant="outlined"
                        onClick={handleOpenIdPreview}
                        size="large"
                        fullWidth={isMobile}
                        style={getActionButtonStyle({
                          backgroundColor: "#ffffff",
                          borderColor: RAMPVER_GREEN,
                          color: RAMPVER_GREEN,
                        })}
                      >
                        Preview ID
                      </Button>

                      {recordingState === "idle" && (
                        <Button
                          variant="contained"
                          startIcon={<VideocamIcon />}
                          onClick={startRecording}
                          disabled={account.checking || checkingAction}
                          size="large"
                          fullWidth={isMobile}
                          style={getActionButtonStyle()}
                        >
                          Start Recording
                        </Button>
                      )}

                      {recordingState === "recording" && (
                        <Button
                          variant="contained"
                          startIcon={<StopIcon />}
                          onClick={stopRecording}
                          size="large"
                          fullWidth={isMobile}
                          style={getActionButtonStyle({
                            backgroundColor: "#ef4444",
                            borderColor: "#ef4444",
                          })}
                        >
                          Stop Recording
                        </Button>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Step 3 */}
        <div
          style={{
            backgroundColor: "#f5f5f5",
            borderRadius: 12,
            padding: isMobile ? 16 : 20,
            marginBottom: 16,
            boxShadow: "0px 3px 1px -2px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 1px 5px 0px rgba(0,0,0,0.12)",
          }}
        >
          <p className="font-poppins" style={{ fontWeight: 700, fontSize: 16, color: "#111827", marginBottom: 8 }}>
            Step 3: Review and Submit Video
          </p>
          <ul className="font-poppins" style={{ listStyleType: "disc", paddingLeft: isMobile ? 20 : 24, color: "#374151", margin: 0 }}>
            <li style={{ marginBottom: 4 }}>
              Review your recording before submitting.
            </li>
            <li>
              You may re-record if needed.
            </li>
          </ul>
          <div style={{ marginTop: 16 }}>
            {recordingState === "preview" && (
              <div style={getButtonGroupStyle({ marginTop: 0, maxWidth: "100%" })}>
                <Button
                  variant="contained"
                  startIcon={<ReplayIcon />}
                  onClick={handleReRecord}
                  disabled={account.checking || checkingAction}
                  size="large"
                  fullWidth={isMobile}
                  style={getActionButtonStyle()}
                >
                  Re-record
                </Button>
                <Button
                  variant="contained"
                  startIcon={<CloudUploadIcon />}
                  onClick={handleUpload}
                  disabled={account.checking || checkingAction}
                  size="large"
                  fullWidth={isMobile}
                  style={getActionButtonStyle()}
                >
                  Submit Recording
                </Button>
              </div>
            )}

            {recordingState === "uploading" && (
              <div style={getButtonGroupStyle({ marginTop: 0, maxWidth: "100%" })}>
                <Button
                  variant="contained"
                  disabled
                  size="large"
                  fullWidth={isMobile}
                  style={getActionButtonStyle({ opacity: 0.75 })}
                >
                  <CircularProgress
                    size={20}
                    style={{ marginRight: 8, color: "#fff" }}
                  />
                  Submitting...
                </Button>
              </div>
            )}

            {recordingState === "success" && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 16,
                  width: "100%",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    color: "#16a34a",
                    justifyContent: "center",
                    textAlign: "center",
                  }}
                >
                  <CheckCircleOutlineIcon />
                  <span className="font-poppins" style={{ fontWeight: 500 }}>
                    {isEmbedded
                      ? "Recording submitted successfully!"
                      : "Success! Your video attestation has been submitted."}
                  </span>
                </div>
                {isEmbedded ? (
                  <Button
                    variant="contained"
                    startIcon={<ReplayIcon />}
                    onClick={handleReRecord}
                    disabled={account.checking || checkingAction}
                    size="large"
                    fullWidth={isMobile}
                    style={getActionButtonStyle()}
                  >
                    Re-record & Resubmit
                  </Button>
                ) : (
                  <span
                    className="font-poppins"
                    style={{ color: "#4b5563", textAlign: "center" }}
                  >
                    Redirecting you back to your dashboard...
                  </span>
                )}
              </div>
            )}

            {recordingState === "error" && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 16,
                  width: "100%",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    color: "#ef4444",
                    textAlign: "center",
                  }}
                >
                  <ErrorOutlineIcon />
                  <span className="font-poppins">{errorMessage}</span>
                </div>
                <div style={getButtonGroupStyle({ marginTop: 0, maxWidth: "100%" })}>
                  <Button
                    variant="contained"
                    startIcon={<ReplayIcon />}
                    onClick={handleReRecord}
                    disabled={account.checking || checkingAction}
                    size="large"
                    fullWidth={isMobile}
                    style={getActionButtonStyle()}
                  >
                    Re-record
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleUpload}
                    disabled={account.checking || checkingAction || !recordedBlob}
                    size="large"
                    fullWidth={isMobile}
                    style={getActionButtonStyle()}
                  >
                    Try Again
                  </Button>
                </div>
              </div>
            )}

            {hasExistingVideo ? (
              <p
                className="font-poppins"
                style={{
                  margin: 0,
                  color: "#4b5563",
                  textAlign: isMobile ? "left" : "center",
                }}
              >
                Your current submitted video is shown below. Use re-record if
                you need to replace it.
              </p>
            ) : (recordingState === "idle" || recordingState === "recording") && (
              <p
                className="font-poppins"
                style={{
                  margin: 0,
                  color: "#4b5563",
                  textAlign: isMobile ? "left" : "center",
                }}
              >
                Once you stop recording, you can review your video here before
                submitting.
              </p>
            )}
          </div>
        </div>
      </Paper>

      {hasExistingVideo ? (
        <Paper style={{ padding: surfacePadding }} elevation={2}>
          <p
            className="font-poppins"
            style={{
              fontWeight: 600,
              fontSize: isMobile ? 16 : 18,
              marginBottom: 16,
              color: "#111827",
            }}
          >
            Your submitted video
          </p>
          <div
            style={{
              width: "100%",
              maxWidth: mediaFrameMaxWidth,
              margin: "0 auto",
            }}
          >
            <div
              style={{
                position: "relative",
                borderRadius: 8,
                overflow: "hidden",
                backgroundColor: "#000",
                paddingTop: mediaFramePaddingTop,
              }}
            >
              <video
                src={existingVideoUrl}
                controls
                playsInline
                preload="metadata"
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              />
            </div>
          </div>
          <div style={getButtonGroupStyle({ marginTop: 16 })}>
            <Button
              variant="contained"
              startIcon={<ReplayIcon />}
              onClick={handleReRecord}
              disabled={account.checking || checkingAction}
              size="large"
              fullWidth={isMobile}
              style={getActionButtonStyle()}
            >
              Re-record
            </Button>
          </div>
        </Paper>
      ) : null}

      {props.nextStep && (
        <Grid item xs={12} style={{ marginTop: 24 }}>
          <div className="flex">
            <div className="btn-back" onClick={props.prevStep}>
              Previous Step
            </div>
            <CustomButton
              text="Continue"
              type="button"
              disabled={
                loadingMeta ||
                account.checking || checkingAction ||
                (recordingState !== "success" && !hasCompletedAttestation)
              }
              buttonTheme="primary"
              onClick={handleContinue}
            />
          </div>
        </Grid>
      )}

      <Dialog
        open={idPreviewOpen}
        onClose={() => setIdPreviewOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle className="font-poppins">
          Show this ID during recording
        </DialogTitle>
        <DialogContent dividers>
          {idPreviewLoading ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: 260,
              }}
            >
              <CircularProgress style={{ color: RAMPVER_GREEN }} />
            </div>
          ) : idPreviewError ? (
            <div
              className="font-poppins"
              style={{
                minHeight: 180,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                color: "#4b5563",
              }}
            >
              {idPreviewError}
            </div>
          ) : idPreviewUrl ? (
            <img
              src={idPreviewUrl}
              alt="Submitted front side of ID"
              style={{
                display: "block",
                width: "100%",
                height: "auto",
                borderRadius: 8,
              }}
            />
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIdPreviewOpen(false)} style={{ color: RAMPVER_GREEN }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }
      `}</style>
    </div>
  );
};

export { VideoAttestationBase as VideoAttestationStep };

const condition = (authUser) => !!authUser;

const VideoAttestation = compose(
  withAuthorization(condition),
  withFirebase
)(VideoAttestationBase);

const VideoAttestationPage = () => (
  <NavigationMenus content={<VideoAttestation />} disableFab />
);

export default VideoAttestationPage;
